import { useCallback, useRef, useState } from "react";
import { ReactLenis } from "lenis/react";
import "lenis/dist/lenis.css";
import { useSite } from "@/lib/SiteContext";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import EditorialMarquee from "@/components/EditorialMarquee";
import Categories from "@/components/Categories";
import Products from "@/components/Products";
import Manifesto from "@/components/Manifesto";
import Occasions from "@/components/Occasions";
import Testimonials from "@/components/Testimonials";
import Instagram from "@/components/Instagram";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import CustomSection from "@/components/CustomSection";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";

export default function HomePage() {
  const lenisRef = useRef(null);
  const [category, setCategory] = useState("all");
  const { sections } = useSite();

  const scrollTo = useCallback((hash) => {
    const lenis = lenisRef.current?.lenis;
    if (lenis) {
      lenis.scrollTo(hash, { offset: -72, duration: 1.4 });
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const goToShop = useCallback(
    (cat) => {
      setCategory(cat);
      scrollTo("#shop");
    },
    [scrollTo],
  );

  const renderers = {
    hero: (sec) => <Hero key="hero" content={sec?.content} onNavigate={scrollTo} />,
    marquee: (sec) => <EditorialMarquee key="marquee" items={sec?.content?.items} />,
    categories: (sec) => <Categories key="categories" content={sec?.content} onSelect={goToShop} />,
    products: (sec) => (
      <Products
        key="products"
        content={sec?.content}
        category={category}
        onCategoryChange={setCategory}
      />
    ),
    manifesto: (sec) => <Manifesto key="manifesto" content={sec?.content} />,
    occasions: (sec) => <Occasions key="occasions" content={sec?.content} onNavigate={scrollTo} />,
    testimonials: (sec) => <Testimonials key="testimonials" content={sec?.content} />,
    instagram: (sec) => <Instagram key="instagram" content={sec?.content} />,
    whatsapp_cta: (sec) => <WhatsAppCTA key="whatsapp_cta" content={sec?.content} />,
  };

  const DEFAULT_ORDER = [
    "hero", "marquee", "categories", "products", "manifesto",
    "occasions", "testimonials", "instagram", "whatsapp_cta",
  ];

  const body = sections
    ? sections.map((sec) =>
        renderers[sec.type] ? (
          renderers[sec.type](sec)
        ) : (
          <CustomSection key={sec.id} section={sec} onNavigate={scrollTo} />
        ),
      )
    : DEFAULT_ORDER.map((type) => renderers[type](null));

  return (
    <ReactLenis root ref={lenisRef} options={{ lerp: 0.09, duration: 1.2 }}>
      <div className="App min-h-screen bg-cream font-sans text-ink antialiased">
        <Header onNavigate={scrollTo} />
        <main>{body}</main>
        <Footer onNavigate={scrollTo} onSelectCategory={goToShop} />
        <BackToTop />
        <FloatingWhatsApp />
      </div>
    </ReactLenis>
  );
}
